import { v } from "convex/values";
import type { Doc } from "../_generated/dataModel";

export type BillingInterval = "month" | "year";
export const billingInterval = v.union(v.literal("month"), v.literal("year"));

type TierPrices = { month: number; year?: number };

// A tier is held one at a time on a ladder; an addon stacks on any tier
// (Stingray will be the first, #71); a pass is a tier's content for a window.
// `sold` is whether the product offers it today. An unsold plan stays here so
// the rows that name it (users.plan, packs.includedInPlans) stay valid with no
// migration; no surface lists it and no action sells it.
type Plan = { sold: boolean } & (
  | { kind: "tier"; rank: number; name: string; monthlyCreateLimit: number | null; priceCents: TierPrices }
  | { kind: "addon"; name: string }
  | { kind: "pass"; name: string; priceCents: number; hours: number }
);

/**
 * Every plan the product knows, by key. The beta sells one subscription,
 * Beta: a higher upload cap and the packs ticked into it. Prices and limits
 * are placeholders, flagged as such on the Plan & billing tab; real numbers
 * are one edit here and nothing else in the codebase holds one. Beta's cap is
 * a number, not `null`, because every upload costs pipeline compute. A pack
 * names the plans it is in (`packs.includedInPlans`); a tier includes only its
 * own packs. Star, Headliner and the One Night pass are unsold until the plan
 * ladder returns with real pricing (#127); a pass already held still plays at
 * queue-grant time (model/billing and tracks.grantQueue).
 */
export const PLANS = {
  free: { kind: "tier", sold: true, rank: 0, name: "Free", monthlyCreateLimit: 5, priceCents: { month: 0 } },
  beta: { kind: "tier", sold: true, rank: 1, name: "Beta", monthlyCreateLimit: 25, priceCents: { month: 999 } },
  star: { kind: "tier", sold: false, rank: 2, name: "Star", monthlyCreateLimit: 5, priceCents: { month: 999, year: 9900 } },
  headliner: { kind: "tier", sold: false, rank: 3, name: "Headliner", monthlyCreateLimit: null, priceCents: { month: 1999, year: 19900 } },
  night: { kind: "pass", sold: false, name: "One night", priceCents: 699, hours: 24 },
} satisfies Record<string, Plan>;

export type PlanKey = keyof typeof PLANS;
export type PlanTier = { [K in PlanKey]: (typeof PLANS)[K]["kind"] extends "tier" ? K : never }[PlanKey];

export const PLAN_KEYS = Object.keys(PLANS) as PlanKey[];
export const planKey = v.union(...PLAN_KEYS.map((key) => v.literal(key)));

export const isTier = (key: PlanKey): key is PlanTier => PLANS[key].kind === "tier";
export const planTier = v.union(...PLAN_KEYS.filter(isTier).map((key) => v.literal(key)));

/** The plans on sale, in ladder order: what every surface that lists plans lists. */
export const SOLD_PLAN_KEYS = PLAN_KEYS.filter((key) => PLANS[key].sold);

/** The plan a refused song is sold in, as the mobile app's unlock sheet shows
 * it, so no client holds a price. */
export type UnlockOffer = {
  tier: PlanTier;
  name: string;
  priceCents: number;
  monthlyCreateLimit: number | null;
};

/** The offer for a song in `plans` (a pack's inclusions): the lowest paid
 * rung on sale among them, or null when nothing on sale includes the song. */
export function unlockOffer(plans: readonly PlanKey[]): UnlockOffer | null {
  const sold = plans.filter(isTier).filter((key) => PLANS[key].sold && key !== "free");
  if (sold.length === 0) return null;
  const tier = sold.reduce((best, key) => (PLANS[key].rank < PLANS[best].rank ? key : best));
  return {
    tier,
    name: PLANS[tier].name,
    priceCents: tierPriceCents(tier, "month"),
    monthlyCreateLimit: PLANS[tier].monthlyCreateLimit,
  };
}

/** The plans a user holds. Every account holds Free; an active or trialing
 * subscription adds its tier, Headliner when the row predates `plan`. Computed
 * from the user row until a second plan can be held at once, when holdings
 * become rows (#71): every rights and shelf decision takes this list, never a
 * subscription field. */
export function activePlans(user: Pick<Doc<"users">, "subscriptionStatus" | "plan">): PlanKey[] {
  const subscribed = user.subscriptionStatus === "active" || user.subscriptionStatus === "trialing";
  return subscribed ? ["free", user.plan ?? "headliner"] : ["free"];
}

/** The tier whose limits apply: the highest rung among the plans held. */
export function highestTier(plans: PlanKey[]): PlanTier {
  return plans.filter(isTier).reduce((best, key) => (PLANS[key].rank > PLANS[best].rank ? key : best), "free");
}

/** What a tier charges per `interval`. Sold tiers are monthly only; the
 * unsold placeholders keep an annual figure for rows subscribed before the
 * beta. A tier with no price at `interval` was never held at it. */
export function tierPriceCents(tier: PlanTier, interval: BillingInterval): number {
  const prices: TierPrices = PLANS[tier].priceCents;
  return prices[interval] ?? prices.month;
}

/** The Stripe prices a checkout can name, by the deployment env var holding
 * each price id. The ids differ per Stripe account, so they are never in code.
 * One entry per plan on sale: adding a price here is what puts a plan on sale,
 * and model/checkout.checkoutOpen requires every id listed. */
export const STRIPE_PRICES = {
  beta_month: { env: "STRIPE_PRICE_BETA_MONTHLY", tier: "beta", interval: "month" },
} as const satisfies Record<string, { env: string; tier: PlanTier; interval: BillingInterval }>;

/** Stripe's product tax code for a pack sold outright: digital audio,
 * downloaded, kept for good. The subscription product carries its own code on
 * the Stripe product; a pack has no product there, so its Checkout line names
 * one. An account with Stripe Tax or Managed Payments refuses a line without
 * it. */
export const PACK_TAX_CODE = "txcd_10401100";

export type StripePriceKey = keyof typeof STRIPE_PRICES;

export type Env = Record<string, string | undefined>;

/** The price id behind a key, from the deployment's env. Throws when unset so
 * a misconfigured deployment fails at checkout rather than charging nothing. */
export function priceIdFor(key: StripePriceKey, env: Env): string {
  const id = env[STRIPE_PRICES[key].env];
  if (!id) throw new Error(`${STRIPE_PRICES[key].env} not set`);
  return id;
}

/** Which tier and interval a Stripe price id means, or null for a price this
 * deployment does not sell: a subscription from before the beta keeps the
 * plan its row already names. */
export function subscriptionForPriceId(priceId: string, env: Env): { tier: PlanTier; interval: BillingInterval } | null {
  for (const price of Object.values(STRIPE_PRICES)) {
    if (env[price.env] === priceId) return { tier: price.tier, interval: price.interval };
  }
  return null;
}
